import { combineComparers, priorityComparer, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { getEntriliaOrgClientCode } from "../../../utilities/clientHelper";
import type {
  ClientProfile,
  ClientTypeFilter,
  SearchResultGroup,
  ToggleableClientProductKey,
} from "./clientProfilesTypes";

export type { ToggleableClientProductKey };

export interface ClientProductOption {
  key: ToggleableClientProductKey;
  name: string;
  enabled: boolean;
  canToggle: boolean;
}

export interface ClientProfileViewModel extends ClientProfile {
  parentOrganizations: ClientProfile[];
  managedFundManagers: ClientProfile[];
  products: ClientProductOption[];
}

const sortByTitleThenCode = combineComparers<ClientProfile>(
  stringComparerBy((c) => c.title),
  stringComparerBy((c) => c.clientCode)
);

const sortByTypeThenTitleThenCode = combineComparers<ClientProfile>(
  priorityComparer((c) => c.type === "FundAdmin"),
  sortByTitleThenCode
);

const getClientPrimaryOrganizationCode = (
  client: ClientProfile,
  organizationCodes: Set<string>
): string | undefined => {
  if (client.type !== "FundManager") {
    return undefined;
  }

  const entriliaOrgCode = getEntriliaOrgClientCode();
  const nonEntriliaOrganization = client.managedByClients.find(
    (code) => code !== entriliaOrgCode && organizationCodes.has(code)
  );

  return nonEntriliaOrganization ?? client.managedByClients.find((code) => organizationCodes.has(code));
};

export const isToggleableProduct = (
  product: ClientProductOption
): product is ClientProductOption & { key: ToggleableClientProductKey; canToggle: true } => {
  return product.canToggle;
};

const buildProducts = (client: ClientProfile): ClientProductOption[] => {
  return [
    {
      key: "investorPortal",
      name: "Investor Portal",
      enabled: client.applications.investorPortal.enabled,
      canToggle: client.applications.investorPortal.supported,
    },
    {
      key: "expenseManagement",
      name: "Expense Management",
      enabled: client.applications.expenseManagement.enabled,
      canToggle: client.applications.expenseManagement.supported,
    },
    {
      key: "entriliaMcp",
      name: "Entrilia MCP",
      enabled: client.applications.entriliaMcp.enabled,
      canToggle: client.applications.entriliaMcp.supported,
    },
    {
      key: "portfolioMonitoring",
      name: "Portfolio Monitoring",
      enabled: client.applications.portfolioMonitoring.enabled,
      canToggle: client.applications.portfolioMonitoring.supported,
    },
  ];
};

export const buildClientProfileViewModel = (
  selectedClient: ClientProfile,
  allClients: ClientProfile[]
): ClientProfileViewModel => {
  const parentOrganizations =
    selectedClient.type === "FundManager"
      ? allClients.filter(
          (client) => client.type === "FundAdmin" && selectedClient.managedByClients.includes(client.clientCode)
        )
      : [];

  const managedFundManagers =
    selectedClient.type === "FundAdmin"
      ? allClients.filter(
          (client) => client.type === "FundManager" && selectedClient.managesClients.includes(client.clientCode)
        )
      : [];

  return {
    ...selectedClient,
    parentOrganizations,
    managedFundManagers,
    products: buildProducts(selectedClient),
  };
};

export const getOrganizations = (clientProfiles: ClientProfile[]): ClientProfile[] =>
  clientProfiles.filter((client) => client.type === "FundAdmin").sort(sortByTitleThenCode);

export const filterClientProfiles = (
  clientProfiles: ClientProfile[],
  clientsByCode: Map<string, ClientProfile>,
  searchTerm: string,
  organizationScope: string,
  clientTypeFilter: ClientTypeFilter
): ClientProfile[] => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const organization = organizationScope ? clientsByCode.get(organizationScope) : undefined;
  const scopeCodes = organization
    ? new Set([organization.clientCode, ...organization.managesClients])
    : new Set(clientProfiles.map((client) => client.clientCode));

  return clientProfiles
    .filter((client) => scopeCodes.has(client.clientCode))
    .filter((client) => clientTypeFilter === "All" || client.type === clientTypeFilter)
    .filter((client) => {
      if (!normalizedSearch) {
        return true;
      }

      return (
        client.title.toLowerCase().includes(normalizedSearch) ||
        client.clientCode.toLowerCase().includes(normalizedSearch)
      );
    })
    .sort(sortByTypeThenTitleThenCode);
};

export const groupFilteredClients = (
  filteredClients: ClientProfile[],
  organizations: ClientProfile[],
  organizationScope: string
): SearchResultGroup[] => {
  if (organizationScope) {
    return [{ key: "scoped-results", clients: filteredClients }];
  }

  const filteredByCode = new Map(filteredClients.map((client) => [client.clientCode, client]));
  const organizationCodes = new Set(organizations.map((organization) => organization.clientCode));
  const groupedFundManagers = new Map<string, ClientProfile[]>();

  filteredClients.forEach((client) => {
    if (client.type !== "FundManager") {
      return;
    }

    const groupKey = getClientPrimaryOrganizationCode(client, organizationCodes) ?? "other";
    const existing = groupedFundManagers.get(groupKey) ?? [];
    existing.push(client);
    groupedFundManagers.set(groupKey, existing);
  });

  const groups: SearchResultGroup[] = [];

  organizations.forEach((organization) => {
    const groupFundManagers = (groupedFundManagers.get(organization.clientCode) ?? []).sort(
      sortByTypeThenTitleThenCode
    );

    const includeGroup = filteredByCode.has(organization.clientCode) || groupFundManagers.length > 0;
    if (!includeGroup) {
      return;
    }

    groups.push({
      key: organization.clientCode,
      organization,
      organizationMatchesFilter: filteredByCode.has(organization.clientCode),
      clients: groupFundManagers,
    });
  });

  const otherClients = (groupedFundManagers.get("other") ?? []).sort(sortByTitleThenCode);
  if (otherClients.length > 0) {
    groups.push({
      key: "other",
      label: "Other",
      clients: otherClients,
    });
  }

  return groups;
};
